import { useContext, useEffect, useMemo, useState } from "react";
import apiService from "../../../services/service";
import API_ROUTES from "../../../api-routes";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";
import { EntityContext } from "..";

function useSetUpProcessModel({ entity }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.login);
  const [serviceLine, setServiceLine] = useState(null);
  const [processType, setProcessType] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [processesTypesData, setProcessesTypesData] = useState([]);
  const [isProcessFetch, setIsProcessFetch] = useState(false);
  const [toggleProcessModal, setToggleProcessModal] = useState(false);
  const [showProcessExistModal, setShowProcessExistModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [utcDate, setUtcDate] = useState(null);

  const createProcess = async (processData, force) => {
    try {
      setLoading(true);
      const { data } = await apiService(API_ROUTES.ADD_PROCESS, "POST", {
        replace: { entity_id: id },
        body: {
          template: processData.template,
          startDate: processData.startDate,
          force,
        },
        schema: z.object({
          entity_id: z.string().uuid(),
          template: z.string().uuid(),
          startDate: z.string().datetime(),
          force: z.boolean().optional(),
        }),
      });
      if (data.processExists) {
        setToggleProcessModal(false);
        setShowProcessExistModal(true);
      }
      if (data.process) {
        const processId = data.process;
        setToggleProcessModal(false);
        setShowProcessExistModal(false);
        navigate(`/process/${processId}/milestones`);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCreateProcess = ({ force = false }) => {
    if (!utcDate) {
      return;
    }
    const processData = {
      template: processType,
      startDate: utcDate,
    };
    createProcess(processData, force);
  };

  const formattedServices =
    entity?.active_services
      ?.map((service) => ({
        label: service.name,
        value: service.id,
      }))
      .filter((service) => service?.label !== "Payroll +") || [];

  const formattedProcessTypes = useMemo(() => {
    const getUpdatedTs = (p) => {
      const keys = [
        "updatedAt",
        "updated_at",
        "modifiedAt",
        "modified_at",
        "updatedOn",
        "updated_on",
        "createdAt",
        "created_at",
      ];
      for (const k of keys) {
        if (p && p[k]) {
          const t = new Date(p[k]).getTime();
          if (!Number.isNaN(t)) return t;
        }
      }
      return 0;
    };

    const normalized =
      processesTypesData
        ?.filter((process) => process.service === serviceLine)
        // Hide only the exact "Payment Processing Support" (case-insensitive)
        .filter(
          (process) =>
            (process?.name || "").trim().toLowerCase() !==
            "payment processing support",
        ) || [];

    // Deduplicate by name: keep the latest-updated variant
    const byName = new Map();
    normalized.forEach((p) => {
      const key = (p?.name || "").trim().toLowerCase();
      const existing = byName.get(key);
      if (!existing || getUpdatedTs(p) > getUpdatedTs(existing)) {
        byName.set(key, p);
      }
    });

    return Array.from(byName.values()).map((process) => ({
      label: process.name,
      value: process.id,
      processId: process.id,
    }));
  }, [processesTypesData, serviceLine]);

  useEffect(() => {
    if (formattedServices.length === 1 && toggleProcessModal) {
      setServiceLine(formattedServices[0].value);
    }
  }, [formattedServices]);
  const handleSelectServiceLine = async (e) => {
    setServiceLine(e.value);
    setProcessType(null);
    setProcessType(e.value);
  };

  const handleOpenProcessModal = async () => {
    setIsProcessFetch(true);
    await fetchProcessTypes();
    setIsProcessFetch(false);
    setToggleProcessModal(true);
  };
  const fetchProcessTypes = async () => {
    const { data } = await apiService(`${API_ROUTES.FETCH_PROCESS_TYPE}`);
    setProcessesTypesData(data);
  };
  const handleCloseModal = () => {
    setToggleProcessModal(false);
    setServiceLine(null);
    setProcessType(null);
    setStartDate(null);
    setShowProcessExistModal(false);
  };
  const hasOnlyPayrollPlus = useMemo(() => {
    const services = entity?.active_services ?? [];
    if (services.length !== 1) return false;
    return services[0]?.name === "Payroll +";
  }, [entity]);

  return {
    serviceLine,
    setServiceLine,
    setProcessType,
    processType,
    startDate,
    setStartDate,
    processesTypesData,
    createProcess,
    handleCreateProcess,
    formattedServices,
    formattedProcessTypes,
    dispatch,
    handleSelectServiceLine,
    toggleProcessModal,
    setToggleProcessModal,
    handleOpenProcessModal,
    isProcessFetch,
    entity,
    handleCloseModal,
    role,
    loading,
    setUtcDate,
    showProcessExistModal,
    hasOnlyPayrollPlus,
  };
}

export default useSetUpProcessModel;
